<script lang="ts" setup>
import DropDown from '@/components/common/DropDown.vue';
import {
  PhFloppyDisk,
  PhListNumbers,
  PhPencilSimple,
  PhPlus,
  PhReceiptX,
  PhTrash,
  PhX,
} from '@phosphor-icons/vue';
import { formatNumber } from 'cesg-shared';
import type { DebitNormSaveEntry, DebitNormsSaveCallbacks } from './types';
import {
  useDebitNormsLogic,
  type UseDebitNormsLogicProps,
} from './logic/useDebitNormsLogic';

const props = defineProps<UseDebitNormsLogicProps>();
const emit = defineEmits<{
  save: [entries: DebitNormSaveEntry[], callbacks: DebitNormsSaveCallbacks];
}>();
const { state, action } = useDebitNormsLogic(props, emit);
</script>

<template>
  <div class="card">
    <div class="card-header">
      <p class="card-header-title">
        <PhListNumbers :size="18" class="mr-2" />Debiteringsnormer
      </p>
      <button
        v-if="state.isEditing"
        class="header-btn cancel-btn"
        :disabled="state.isSaving"
        @click="action.cancel()"
      >
        <PhX :size="15" />
        Avbryt
      </button>
      <button
        v-if="state.mode !== 'read-only'"
        class="header-btn edit-btn"
        :class="{ 'is-active': state.isEditing }"
        :disabled="state.isSaving"
        @click="state.isEditing ? action.requestSave() : action.startEdit()"
      >
        <span v-if="state.isSaving" class="btn-spinner" />
        <PhFloppyDisk v-else-if="state.isEditing" :size="15" />
        <PhPencilSimple v-else :size="15" />
        {{ state.isEditing || state.isSaving ? 'Spara' : 'Redigera' }}
      </button>
    </div>
    <div class="card-content p-0">
      <div
        v-if="state.debitNorms.length === 0 && !state.isEditing"
        class="empty-state"
      >
        <PhReceiptX :size="32" />
        <span>Inga debiteringsnormer</span>
      </div>
      <table v-else class="table is-fullwidth is-striped is-hoverable mb-0">
        <thead>
          <tr>
            <th class="col-account">Konto</th>
            <th>Benämning</th>
            <th class="col-rate">kr/h</th>
            <th v-if="state.isEditing" class="col-action"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="dn in state.debitNorms" :key="dn.id">
            <td class="col-account account-number">{{ dn.account.number }}</td>
            <td class="col-name" :data-tooltip="dn.account.name">
              <span class="name-text">{{ dn.account.name }}</span>
            </td>
            <td class="col-rate">
              <template v-if="state.isEditing">
                <span class="rate-cell-wrap">
                  <input
                    class="inline-input rate-input"
                    type="text"
                    inputmode="numeric"
                    :value="formatNumber(dn.rate)"
                    :size="formatNumber(dn.rate).length || 3"
                    @change="
                      action.updateEntry(
                        dn.id,
                        ($event.target as HTMLInputElement).value,
                      )
                    "
                  />
                  kr/h
                </span>
              </template>
              <template v-else
                ><span class="rate-cell-wrap">
                  {{ formatNumber(dn.rate) }} kr/h
                </span>
              </template>
            </td>
            <td v-if="state.isEditing" class="col-action">
              <button
                class="row-delete-btn"
                @click="action.deleteEntry(dn.id)"
              >
                <PhTrash :size="13" />
              </button>
            </td>
          </tr>
          <tr v-if="state.isEditing" class="add-row">
            <td class="col-account" colspan="2">
              <DropDown
                :items="state.accountItems"
                :model-value="state.newEntry.accountNumber"
                label="Välj konto..."
                teleport
                @update:model-value="
                  action.updateNewEntry('accountNumber', $event as string)
                "
              />
            </td>
            <td class="col-rate">
              <span class="rate-cell-wrap">
                <input
                  class="inline-input rate-input"
                  type="text"
                  inputmode="numeric"
                  placeholder="0"
                  :value="state.newEntry.rate"
                  size="3"
                  @change="
                    action.updateNewEntry(
                      'rate',
                      ($event.target as HTMLInputElement).value,
                    )
                  "
                  @keydown.enter="
                    action.updateNewEntry(
                      'rate',
                      ($event.target as HTMLInputElement).value,
                    );
                    action.addEntry();
                  "
                />
                kr/h
              </span>
            </td>
            <td class="col-action">
              <button class="row-add-btn" @click="action.addEntry()">
                <PhPlus :size="13" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 2;
}

.card {
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.header-btn {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  align-self: center;
  height: auto;
  width: auto;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: #f5f5f7;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.edit-btn.is-active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.edit-btn.is-active:disabled {
  background: color-mix(in srgb, var(--primary-color) 40%, white);
  border-color: color-mix(in srgb, var(--primary-color) 40%, white);
  color: #fff;
  cursor: not-allowed;
}

.btn-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: btn-spin 0.6s linear infinite;
  flex-shrink: 0;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.cancel-btn {
  margin-right: 0.4rem;
}

.cancel-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.inline-input {
  background: transparent;
  border: none;
  box-shadow: inset 0 -1px 0 #aaa;
  outline: none;
  padding: 0;
  margin: 0;
  height: 1.5em;
  vertical-align: middle;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: 1.5;
  color: inherit;
  transition: box-shadow 0.15s;
}

.inline-input:hover {
  box-shadow: inset 0 -1px 0 #666;
}

.inline-input:focus {
  box-shadow: inset 0 -1px 0 var(--primary-color);
}

.inline-input::placeholder {
  color: #bbb;
}

.rate-input {
  text-align: right;
  width: 4em;
}

.rate-cell-wrap {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  gap: 0.35em;
}

.account-input {
  min-width: 0;
}

.name-input {
  min-width: 0;
  width: 100%;
}

.col-account {
  width: 5rem;
}

.col-name {
  max-width: 0;
  position: relative;
}

.table .col-name {
  overflow: visible;
}

.name-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-name::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  background: #1a1a1a;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 500;
  white-space: nowrap;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 50;
}

.col-name:hover::after {
  opacity: 1;
}

.col-rate {
  width: 7rem;
  text-align: right;
  white-space: nowrap;
}

th.col-rate {
  text-align: right;
}

.col-action {
  width: 3rem;
  text-align: right;
  vertical-align: middle;
}

.account-number {
  font-variant-numeric: tabular-nums;
  color: #666;
  font-size: 0.85rem;
}

.table thead th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  box-shadow: inset 0 -2px 0 #dbdbdb;
  border-bottom: none;
}

.table th,
.table td {
  padding: 0.6rem 0.85rem;
  vertical-align: middle;
  line-height: 1.5;
}

.table tbody tr {
  height: 2.7rem;
}

.table tbody td {
  overflow: hidden;
}

.row-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5em;
  height: 1.5em;
  border: none;
  border-radius: 50%;
  margin-left: auto;
  background: #fee2e2;
  color: #dc2626;
  cursor: pointer;
  transition: background 0.15s;
}

.row-delete-btn:hover {
  background: #fca5a5;
}

.row-add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5em;
  height: 1.5em;
  border: none;
  border-radius: 50%;
  margin-left: auto;
  background: var(--primary-color);
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s;
}

.row-add-btn:hover {
  opacity: 0.85;
}

.add-row {
  background: color-mix(in srgb, var(--primary-color) 5%, white);
  border-top: 1.5px dashed var(--primary-color);
}

.add-row > td {
  border-bottom: none;
  position: sticky;
  bottom: 0;
  background: color-mix(in srgb, var(--primary-color) 5%, white);
  box-shadow: 0 -1px 0 var(--primary-color);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  color: #aaa;
  font-size: 0.9rem;
}
</style>
